import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import '../../../css/FlyoutTeacherScorePanel.scss';

import RichTextEditor, { MINIMUM_TOOLBAR } from '../RichTextEditor';

import { ContentType, FlyoutOpenState, LessonSlideIds, ScoreState } from '../../../Constants';

import timeManager from '../../managers/TimeManager';

import { register } from '../../../i18n';

import navMenuService from '../../services/NavMenuService';
import responseService from '../../services/ResponseService';
import scoringService from '../../services/ScoringService';
import utilsService from '../../services/UtilsService';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';
import useWirisParser from '../../../hooks/useWirisParser';

import AiSuggestedScoreButton from '../AiSuggestedScoreButton';
import { isRubricCheckboxScoring } from '../../utils/rubricCheckboxScoring';
import ExpandableRubricScoringTable from './ExpandableRubricScoringTable';
import RubricScoreTable from './RubricScoreTable';
import RubricViewForScoring from './RubricViewForScoring';

const t = register('TeacherScoringPanel');
const t2 = register('AriaLabels');

const TeacherScorePanel = observer(({ lessonElementId }) => {
  const {
    responseManager,
    lessonManager,
    scoringManager,
    // navMenuManager
  } = useContext(MobXProviderContext);

  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const model = lessonManager.getSlideModel(lessonElementId);
  const teacherScoreRef = useRef();
  const [isRubricExpanded, setIsRubricExpanded] = useState(false);

  // On mount, if rubric is collapsed, reset flyout to OPEN
  useEffect(() => {
    if (!isRubricExpanded) {
      const closeFirst = false;
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN, lessonElementId);
    }
  }, []);

  // On mount, check if scores match AI and set state (only if not already set)
  useEffect(() => {
    const checkAndSetIfChangedFromAiScore = async () => {
      if (!lessonElementState) {
        return;
      }
      const hasChangedFromAiScore = typeof lessonElementState.hasChangedFromAiScore === 'boolean' ? (
        lessonElementState.hasChangedFromAiScore
      ) : (
        await scoringService.checkIfChangedFromAiScore(lessonElementId, lessonElementState.scoreState)
      );

      if (hasChangedFromAiScore !== null) {
        lessonElementState.setHasChangedFromAiScore(hasChangedFromAiScore);
      }
    };

    checkAndSetIfChangedFromAiScore();
  }, [lessonElementId]);

  const handleManualScoreChange = (event) => {
    let str = event.target.value;
    if (utilsService.ensureNumericInput(str)) {
      const num = parseFloat(str);
      const maxScore = parseFloat(lessonElementState.maxScore);

      if (num > maxScore) {
        str = maxScore.toFixed(2);
      }
      if (num < 0) {
        str = '';
      }
      scoringService.setTempScore(lessonElementId, str);

      markAsChangedFromAiScore();
    }
  };

  // Mark that scores have been manually changed from AI
  const markAsChangedFromAiScore = () => {
    if (lessonElementState.scoreState === ScoreState.AI_SCORED ||
        lessonElementState.scoreState === ScoreState.AI_OVERRIDDEN) {
      lessonElementState.setHasChangedFromAiScore(true);
    }
  };

  const handleBlur = (event) => {
    const str = event.target.value;
    if (utilsService.isNullOrEmpty(str) || str.trim() === '.') {
      scoringService.setTempScore(lessonElementId, '');
    }
  };

  let teacherComment = '';

  if (lessonElementState && !utilsService.isNullOrEmpty(lessonElementState.comment)) {
    teacherComment = lessonElementState.comment;
  }

  const textRef = useRef();
  useWirisParser(textRef);

  const isSummary = (lessonManager.currentLessonElementId === LessonSlideIds.SUMMARY_PAGE);

  const rubricScored = (lessonElementState && (lessonElementState.scoreState === ScoreState.MANUALLY_SCORED ||
    lessonElementState.scoreState === ScoreState.NOT_SCORED || lessonElementState.scoreState === ScoreState.AI_SCORED ||
    lessonElementState.scoreState === ScoreState.AI_PENDING || lessonElementState.scoreState === ScoreState.AI_OVERRIDDEN) &&
    scoringManager.rubricMap.has(lessonElementId));

  const time = (lessonElementState && lessonElementState.viewedTime && lessonElementState.viewedTime > 0) ?
    utilsService.secondsToMMSS(lessonElementState.viewedTime) : (timeManager.getViewTime(lessonElementId) > 0) ?
      utilsService.secondsToMMSS(timeManager.getViewTime(lessonElementId)) : '00:00';

  useEffect(() => {
    const rubric = scoringManager.rubricMap.get(lessonElementId);
    const skipPointsFocus = Boolean(rubric && isRubricCheckboxScoring(rubric));
    if (!skipPointsFocus && teacherScoreRef?.current) {
      teacherScoreRef.current.focus();
    }
  }, [lessonElementId, model, rubricScored, scoringManager]);

  const fullCreditButtonRef = useRef();

  useAccessibilityClick(fullCreditButtonRef, (e) => {
    const maxScore = lessonElementState.maxScore ? parseFloat(lessonElementState.maxScore) : 0;
    if (lessonElementState.maxScore !== scoringManager.getTempScore(lessonElementId)) {
      scoringService.setTempScore(lessonElementId, maxScore);
      markAsChangedFromAiScore();
    }
  });

  const fullCreditRubricButtonRef = useRef();

  useAccessibilityClick(fullCreditRubricButtonRef, (e) => {
    scoringService.setCategoryFullCreditFromTable(lessonElementId);
    markAsChangedFromAiScore();
  });

  const rubricToggleButtonRef = useRef();

  useAccessibilityClick(rubricToggleButtonRef, () => {
    const closeFirst = false;
    const _isRubricExpanded = !isRubricExpanded;
    setIsRubricExpanded(_isRubricExpanded);

    // If expanding rubric, make flyout OPEN_WIDER, otherwise make it OPEN
    if (_isRubricExpanded) {
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN_WIDER, lessonElementId);
    } else {
      navMenuService.toggleFlyoutPanel(closeFirst, FlyoutOpenState.OPEN, lessonElementId);
    }
  });

  const isKeyboarding = model.type === ContentType.KEYBOARDING.type;

  const allowShowTeacherScorePanelHeader = !isKeyboarding;

  const allowExpandableRubricScoringTable = (
    lessonElementState?.scoreState === ScoreState.AI_SCORED ||
    lessonElementState?.scoreState === ScoreState.AI_OVERRIDDEN
  );

  const rubricModel = scoringManager.rubricMap.get(lessonElementId);
  const hideManualPointsEntryUi = Boolean(rubricModel && isRubricCheckboxScoring(rubricModel));

  return !lessonElementState ? null : (
    <div className='teacher-score-panel flyout-panel' id={`${lessonElementId}-question-view`}>
      {allowShowTeacherScorePanelHeader && (
        <h2 className='panel-header'>
          {isSummary ? '' : (model.unscored) ? t('headerLabelUnscored') : t('headerLabelScore')}
          {/* TODO unused */}
          {/* {(lessonElementState?.hasChangedFromAiScore === false) && (
            <div aria-label={t('aiScoreLabel')} className='ai-score-label'>
              {t('aiScoreLabel')}
            </div>
          )} */}
          {(lessonElementState?.scoreState === ScoreState.AI_PENDING) && (
            <div aria-label={t('aiPendingLabel')} className='ai-score-label'>
              {t('aiPendingLabel')}
            </div>
          )}
          {/* TODO unused */}
          {/* {(lessonElementState?.hasChangedFromAiScore === true) && (
            <div aria-label={t('aiOverriddenLabel')} className='ai-score-label'>
              {t('aiOverriddenLabel')}
            </div>
          )} */}
        </h2>
      )}
      <div className='ui-stack'>
        <div className='top-ui'>
          {(lessonElementState && model && model.isTestItem) ? (
            <>
              <div className='teacher-score-wrapper'>
                <div className='teacher-score-container'>
                  {(scoringManager.tempScores.has(lessonElementId) && !model.unscored) && (
                    <div className='teacher-score-container'>
                      <div className='teacher-score'>
                        {!hideManualPointsEntryUi && (
                          <>
                            <span>{t('points')}:&nbsp;</span>
                            <input ref={teacherScoreRef}
                              aria-label={t2('scoreoverridefield')}
                              disabled={rubricScored || model.rubricEntityId}
                              onBlur={handleBlur}
                              onChange={handleManualScoreChange}
                              type='text'
                              value={scoringManager.getTempScore(lessonElementId)} />
                            <span>/</span><span>{lessonElementState.maxScore}</span>
                            {(typeof lessonElementState.hasChangedFromAiScore === 'boolean') && (
                              <div className='score-type-label'>
                                {lessonElementState.hasChangedFromAiScore === false ? t('aiSuggestedScore') : t('manualScore')}
                              </div>
                            )}
                          </>
                        )}
                        <div className='flyout-score-panel-credit-buttons'>
                          {lessonElementState.maxScore && model.type !== ContentType.LIKERT.type && (
                            <>
                              <AiSuggestedScoreButton lessonElementId={lessonElementId} />
                              {rubricScored ? (
                                <button ref={fullCreditRubricButtonRef} aria-label={t2('givefullcreditbutton')}
                                  className='fullCreditButton flyoutScorePanelCreditButton primary'>
                                  <div aria-hidden className='label'>{t('giveFullCreditLabel')}</div>
                                </button>
                              ) : (
                                <button ref={fullCreditButtonRef} aria-label={t2('givefullcreditbutton')}
                                  className='fullCreditButton flyoutScorePanelCreditButton primary'>
                                  <div aria-hidden className='label'>{t('giveFullCreditLabel')}</div>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {(rubricScored && !allowExpandableRubricScoringTable) && (
                    <RubricViewForScoring lessonElementId={lessonElementId} onScoreChange={markAsChangedFromAiScore} />
                  )}
                  {(rubricScored && !model.unscored) && (
                    <>
                      {allowExpandableRubricScoringTable ? (
                        <div className='expandable-rubric-section-wrapper'>
                          <div aria-label={t('rubricCriteria')} className='resultSubHeader rubric-criteria'>
                            {t('rubricCriteria')}
                            <button
                              ref={rubricToggleButtonRef}
                              className='link-button'
                            >
                              {isRubricExpanded ? t('collapseRubric') : t('expandRubric')}
                            </button>
                          </div>
                          <ExpandableRubricScoringTable
                            editable={true}
                            expanded={isRubricExpanded}
                            lessonElementId={lessonElementId}
                            onScoreChange={markAsChangedFromAiScore}
                            showJustificationWhenCollapsed={false}
                          />
                        </div>
                      ) : (
                        <RubricScoreTable
                          lessonElementId={lessonElementId}
                          onScoreChange={markAsChangedFromAiScore}
                        />
                      )}
                    </>
                  )}
                  <div className='teacher-usage-container'>
                    <div className='teacher-usage'><span>{t('usage')}:&nbsp;</span><span className='usage-value'>{time}</span> </div>
                  </div>
                </div>
              </div>
              <div className='teacher-notes-wrapper'>
                <div className='teacher-notes-container'>
                  <div className='teacher-notes-textbox-wrapper'>
                    <div className='teacher-comments-label'>{t('teacherComments')}:</div>
                    <RichTextEditor
                      data={teacherComment}
                      onBlur={(data) => {
                        responseService.teacherNotesChanged(data, lessonElementId);
                      }} toolbar={MINIMUM_TOOLBAR} />
                  </div>
                </div>
              </div>
            </>
          )
            :
            (lessonElementState && model && model.type === ContentType.STUDENT_INSTRUCTION.type) ? (
              <>
                <div className='teacher-score-wrapper'>

                  <div className='teacher-score-container'>
                    <div className='teacher-usage-container'>
                      <div className='teacher-usage'><span>{t('usage')}:&nbsp;</span><span className='usage-value'>{time}</span></div>
                    </div>
                  </div>

                </div>
                <div className='teacher-notes-wrapper'>
                  <div className='teacher-notes-container'>
                    <div className='teacher-notes-textbox-wrapper'>
                      <div className='teacher-comments-label'>{t('teacherComments')}:</div>
                      <RichTextEditor
                        data={teacherComment}
                        onBlur={(data) => {
                          responseService.teacherNotesChanged(data, lessonElementId);
                        }} toolbar={MINIMUM_TOOLBAR} />
                    </div>
                  </div>
                </div>
              </>
            )
              : (
                <div className='no-teacher-notes' />
              )}
        </div>
      </div>
    </div>
  );
});

export default TeacherScorePanel;
